import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useI18n, LANGUAGES } from "../lib/i18n";
import { Layout } from "../components/Layout";
import { VerifyBadge, CustomBadge } from "../components/Badges";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Star, MapPin, Briefcase, Translate, ChatCircle, PaperPlaneTilt } from "@phosphor-icons/react";
import { toast, Toaster } from "sonner";

function MediaCard({ m }) {
  const { t } = useI18n();
  const [lang, setLang] = useState(m.spoken_language || "en");
  const langs = Object.keys(m.transcript || {});
  return (
    <div className="as-card !p-0 overflow-hidden flex flex-col" data-testid={`media-card-${m.id}`}>
      {m.type === "image" ? (
        <img src={m.data_url} alt={m.title} className="w-full h-56 object-cover" />
      ) : (
        <video src={m.data_url} controls className="w-full h-56 bg-black" />
      )}
      <div className="p-4 flex-1 flex flex-col">
        <div className="font-bold text-[var(--as-primary)]" style={{fontFamily:"Inter"}}>{m.title}</div>
        <div className="text-sm text-[var(--as-text-secondary)] mt-1 line-clamp-2">{m.description}</div>
        {(m.skill_tags || []).length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {m.skill_tags.map((s) => <span key={s} className="as-badge-skill !text-xs">{s}</span>)}
          </div>
        )}
        {langs.length > 0 && (
          <div className="mt-4 pt-3 border-t border-[var(--as-border)]">
            <div className="flex items-center gap-2 mb-2">
              <Translate size={16} weight="duotone" />
              <span className="text-xs font-bold uppercase tracking-wider text-[var(--as-text-secondary)]">{t("transcript")}</span>
              <select className="ml-auto text-xs font-bold border border-[var(--as-border)] rounded px-2 py-1 bg-white" value={lang} onChange={(e) => setLang(e.target.value)} data-testid={`transcript-lang-${m.id}`}>
                {LANGUAGES.filter((l) => langs.includes(l.code)).map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
              </select>
            </div>
            <p className="text-sm text-[var(--as-text)] leading-relaxed">{m.transcript[lang]}</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default function ProfilePage() {
  const { id } = useParams();
  const { t } = useI18n();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [reviewText, setReviewText] = useState("");
  const [rating, setRating] = useState(5);

  const load = () => api.get(`/profile/${id}`).then(({ data }) => setData(data));
  useEffect(() => { load(); }, [id]);

  const submitReview = async (e) => {
    e.preventDefault();
    try {
      await api.post("/reviews", { reviewee_id: id, rating: Number(rating), text: reviewText });
      toast.success("Review posted");
      setReviewText(""); setRating(5);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
  };

  if (!data) return <Layout><div className="p-10 text-center text-[var(--as-text-secondary)]">Loading…</div></Layout>;
  const { user: u, media, reviews, average_rating, review_count } = data;

  return (
    <Layout>
      <Toaster richColors position="top-center" />
      <div className="max-w-5xl mx-auto px-6 py-10 space-y-8">
        <div className="as-card p-8">
          <div className="flex flex-wrap items-start gap-6">
            <div className="w-20 h-20 rounded-2xl bg-[var(--as-bg-alt)] grid place-items-center text-3xl font-extrabold text-[var(--as-primary)]" style={{fontFamily:"Inter"}}>
              {u.name?.[0] || "?"}
            </div>
            <div className="flex-1">
              <div className="text-xs font-bold uppercase tracking-[0.18em] text-[var(--as-accent)]">{u.role.replace("_", " ")}</div>
              <h1 className="text-3xl font-bold text-[var(--as-primary)]" style={{fontFamily:"Inter"}} data-testid="profile-name">{u.name}</h1>
              {u.headline && <p className="text-base text-[var(--as-text-secondary)] mt-1">{u.headline}</p>}
              <div className="flex flex-wrap items-center gap-3 mt-3">
                {(u.city || u.state) && (
                  <span className="inline-flex items-center gap-1.5 text-sm text-[var(--as-text-secondary)]">
                    <MapPin size={16} weight="duotone" />{[u.city, u.state, u.country].filter(Boolean).join(", ")}
                  </span>
                )}
                {u.years_experience > 0 && (
                  <span className="inline-flex items-center gap-1.5 text-sm text-[var(--as-text-secondary)]">
                    <Briefcase size={16} weight="duotone" />{u.years_experience} yrs
                  </span>
                )}
                <span className="inline-flex items-center gap-1.5 text-sm text-[var(--as-text-secondary)]">
                  <Star size={16} weight="fill" color="#F59E0B" />{average_rating || "—"} ({review_count})
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-2 mt-3">
                <VerifyBadge status={u.kyc_status} />
                {(u.badges || []).map((b) => <CustomBadge key={b} label={b} />)}
              </div>
              {(u.skills || []).length > 0 && (
                <div className="flex flex-wrap gap-2 mt-4">
                  {u.skills.map((s) => <span key={s} className="as-badge-skill" data-testid={`profile-skill-${s.toLowerCase().replace(/\s+/g,'-')}`}>{s}</span>)}
                </div>
              )}
            </div>
          </div>

          {u.bio && <p className="mt-6 text-[var(--as-text)] leading-relaxed">{u.bio}</p>}

          {u.role === "msme" && (
            <div className="mt-6 grid sm:grid-cols-3 gap-4">
              {u.company_name && <div className="text-sm"><div className="text-xs font-bold uppercase text-[var(--as-text-secondary)]">{t("company_name")}</div><div className="font-bold mt-1">{u.company_name}</div></div>}
              {u.team_size && <div className="text-sm"><div className="text-xs font-bold uppercase text-[var(--as-text-secondary)]">{t("team_size")}</div><div className="font-bold mt-1">{u.team_size}</div></div>}
              {u.sectors?.length > 0 && <div className="text-sm"><div className="text-xs font-bold uppercase text-[var(--as-text-secondary)]">{t("sectors")}</div><div className="font-bold mt-1">{u.sectors.join(", ")}</div></div>}
            </div>
          )}
        </div>

        <div>
          <h2 className="text-xl font-bold text-[var(--as-primary)] mb-4" style={{fontFamily:"Inter"}}>{t("media_gallery")}</h2>
          {media.length === 0 ? (
            <p className="text-[var(--as-text-secondary)]">{t("no_media")}</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {media.map((m) => <MediaCard key={m.id} m={m} />)}
            </div>
          )}
        </div>

        <div>
          <h2 className="text-xl font-bold text-[var(--as-primary)] mb-4" style={{fontFamily:"Inter"}}>{t("reviews")} ({review_count})</h2>
          {reviews.length > 0 && (
            <ul className="space-y-3">
              {reviews.map((r) => (
                <li key={r.id} className="as-card p-4">
                  <div className="flex items-center justify-between">
                    <div className="font-bold">{r.reviewer_name}</div>
                    <div className="flex items-center gap-1 text-amber-500"><Star size={16} weight="fill" />{r.rating}</div>
                  </div>
                  {r.text && <p className="text-sm text-[var(--as-text-secondary)] mt-1">{r.text}</p>}
                </li>
              ))}
            </ul>
          )}
          {user && user.id !== id && (
            <form onSubmit={submitReview} className="as-card p-5 mt-4 space-y-3" data-testid="review-form">
              <div className="flex items-center gap-3">
                <ChatCircle size={20} weight="duotone" />
                <span className="font-bold text-[var(--as-primary)]">Leave a review</span>
              </div>
              <div className="flex items-center gap-3">
                <label className="as-label !mb-0">{t("rating")}</label>
                <select className="as-input !w-24 !py-2 !text-base" value={rating} onChange={(e) => setRating(e.target.value)} data-testid="review-rating">
                  {[5,4,3,2,1].map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
              <textarea className="as-input min-h-[70px]" value={reviewText} onChange={(e) => setReviewText(e.target.value)} placeholder="Optional…" data-testid="review-text" />
              <button type="submit" className="btn-primary" data-testid="review-submit">
                <PaperPlaneTilt size={18} weight="duotone" /> Post review
              </button>
            </form>
          )}
        </div>

        <div className="text-center">
          <Link to="/search" className="btn-ghost">← Back to search</Link>
        </div>
      </div>
    </Layout>
  );
}
